import { useEffect, useState } from 'react'
import { customerData, CustomerReceiptData } from '../../utilities/CustomerData'
import { useNavigate } from 'react-router-dom'
import { apiURL } from '../../utilities/URLs'
import { refreshAccessToken } from '../../utilities/extensions'
import posthog from 'posthog-js'

export function PaymentConfirmation({ isOpen, onClose, _customerReceipt, isShippo, isPublicPaymentLink = false, publicPaymentToken = '' }) {
  const navigate = useNavigate()
  const customerReceipt = new CustomerReceiptData(_customerReceipt)

  const handleDownloadReceipt = async () => {
    try {
      const newURLResponse = isPublicPaymentLink
        ? await fetch(`${apiURL}/api/publicGenerateS3URL?paymentToken=${encodeURIComponent(publicPaymentToken)}&url=${encodeURIComponent(customerReceipt.receiptLinkURL)}`)
        : await fetch(`${apiURL}/api/generateS3URL?url=${encodeURIComponent(customerReceipt.receiptLinkURL)}`, {
          headers: {
            'Authorization': customerData.accessToken
          }
        })
      const newURLData = await newURLResponse.json()
      if (newURLData.response.error) {
        if (!isPublicPaymentLink && newURLData.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          refreshedToken && refreshedToken.length > 0 ? await handleDownloadReceipt() : navigate('/', { state: { expired: true } })
        } else
          console.error(newURLData.response.error)
      } else {
        const response = await fetch(newURLData.response.s3Url)
        const blob = await response.blob()
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `${customerReceipt.receiptNumber}.pdf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        URL.revokeObjectURL(link.href)

        if (isPublicPaymentLink)
          navigate('/')
        else
          navigate('/dashboard', { state: { activeTab: 1 } })
      }
    } catch (error) {
      console.error('Error downloading the receipt:', error)
    }
  }

  return (
    isOpen && (
      <div className='fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50'>
        <div className='bg-white shadow-md rounded-lg p-6 lg:w-full w-11/12 max-w-lg text-center relative'>
          <h2 className='text-2xl font-semibold mb-2'>Payment Successful!</h2>
          <p className='text-gray-600 mb-6'>
            Thank you for your payment. The transaction was completed successfully, and your receipt details are below.
          </p>
          <div className='mb-6 text-left'>
            <h3 className='text-lg font-medium mb-2'>Receipt Details</h3>
            <ul className='text-sm'>
              <li><strong>Receipt Number:</strong> {customerReceipt.receiptNumber}</li>
              <li><strong>Payment Type:</strong> {customerReceipt.receiptPaymentType}</li>
              <li><strong>Payment Amount:</strong> ${Number(customerReceipt.receiptPaymentAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</li>
            </ul>
          </div>
          <div className='flex justify-between'>
            <button onClick={handleDownloadReceipt} className='bg-primaryColor hover:bg-primaryDarkColor transition duration-200 text-white px-4 py-2 rounded'>Download Receipt</button>
            <button onClick={onClose} className='bg-secondaryColor hover:bg-secondaryDarkColor transition duration-200 text-white px-4 py-2 rounded'>{isShippo ? 'Close' : (isPublicPaymentLink ? 'Close' : 'Get My Refund')}</button>
          </div>
        </div>
      </div>
    )
  )
}

export function PaymentDeclined({ isOpen, setIsOpen, paymentCode, paymentError, retriesRemaining }) {
  return (
    isOpen && (
      <div className='fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50'>
        <div className='bg-white shadow-md rounded-lg p-6 lg:w-full w-11/12 max-w-lg text-center relative'>
          <h2 className='text-2xl font-semibold mb-2'>Payment Declined!</h2>
          <p className='text-gray-600 mb-6 text-left'>{paymentError}</p>
          {retriesRemaining !== 0 && (
            <>
              <p className='text-gray-600 mb-6'>You have {retriesRemaining} retries remaining.</p>
              <p className='text-gray-600 mb-6'>ERR CODE {paymentCode}</p>
            </>
          )}
          <div className='flex justify-center'>
            <button onClick={() => setIsOpen(false)} className='bg-primaryColor hover:bg-primaryDarkColor transition duration-200 text-white px-4 py-2 rounded'>Ok</button>
          </div>
        </div>
      </div>
    )
  )
}

export function AddPaymentConfirmation({ isOpen }) {
  const navigate = useNavigate()

  return (
    isOpen && (
      <div className='fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50'>
        <div className='bg-white shadow-md rounded-lg p-6 lg:w-full w-11/12 max-w-lg text-center relative'>
          <h2 className='text-2xl font-semibold mb-2'>Payment Information Added!</h2>
          <p className='text-gray-600 mb-6'>Thank you for adding your payment method</p>
          <p className='text-gray-600 mb-6'>Remember, you can pay your invoice anytime from the Documents Center and 'Pay Invoice'</p>
          <div className='flex justify-center'>
            <button onClick={() => navigate('/dashboard')} className='bg-primaryColor hover:bg-primaryDarkColor transition duration-200 text-white px-4 py-2 rounded'>Return To Dashboard</button>
          </div>
        </div>
      </div>
    )
  )
}

export function AddPaymentDeclined({ isOpen, setIsOpen, paymentCode, paymentError, retriesRemaining }) {
  return (
    isOpen && (
      <div className='fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50'>
        <div className='bg-white shadow-md rounded-lg p-6 lg:w-full w-11/12 max-w-lg text-center relative'>
          <h2 className='text-2xl font-semibold mb-2'>Payment Information Declined!</h2>
          <p className='text-gray-600 mb-6 text-left'>{paymentError}</p>
          {retriesRemaining !== 0 && (
            <>
              <p className='text-gray-600 mb-6'>You have {retriesRemaining} retries remaining.</p>
              <p className='text-gray-600 mb-6'>ERR CODE {paymentCode}</p>
            </>
          )}
          <div className='flex justify-center'>
            <button onClick={() => setIsOpen(false)} className='bg-primaryColor hover:bg-primaryDarkColor transition duration-200 text-white px-4 py-2 rounded'>Ok</button>
          </div>
        </div>
      </div>
    )
  )
}

export function GenerateInvoicePostPaid({ isOpen, setIsOpen, setShowTOS, handleSignedTOS, setTOSCustomerFee, setTOSOptionClicked, generatingInvoice }) {
  const handleSelectFeeRoute = (fee, optionClicked) => {
    if (customerData.customerTOSData.tosLinkURL.length === 0) {
      setTOSCustomerFee(fee)
      setTOSOptionClicked(optionClicked)
      setShowTOS(true)
    } else
      handleSignedTOS(fee, optionClicked)
  }

  useEffect(() => {
    if (isOpen)
      posthog.startSessionRecording()
    else
      posthog.stopSessionRecording()
  }, [isOpen])

  return (
    isOpen && (
      <div className='fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-30'>
        <div className='bg-white shadow-md rounded-lg p-10 lg:w-full w-11/12 lg:max-w-[65%] text-center relative overflow-auto max-sm:h-[90%]'>
          <button onClick={() => setIsOpen(false)} className='absolute right-6 top-6 text-red-700 hover:text-red-900 transition-colors duration-200'>
            <svg xmlns='http://www.w3.org/2000/svg' className='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M6 18L18 6M6 6l12 12' />
            </svg>
          </button>
          <h2 className='text-2xl font-semibold mb-4'>SETC Pros Fee Options</h2>
          <p className='text-gray-600 mb-8 text-start'>Great news! We are receiving numerous requests from customers asking for a discount if they pay their invoices upfront. We've heard you, and are happy to announce that you can pay your invoice now with a significant discount!</p>
          <p className='text-gray-600 mb-8 font-bold text-xl'>Your refund amount is: ${customerData.customerLeadData.leadAmount.toLocaleString()}</p>
          <div className='flex flex-row max-sm:flex-col justify-between lg:items-stretch lg:space-x-8'>
            <div className='flex flex-col items-center flex-1'>
              <div className='mb-4'>
                <p className='text-gray-600 font-bold text-xl text-start'>Option 1</p>
                <p className='text-gray-600 mt-2 text-left'>Pay your SETC Pros fee upfront and take advantage of a significant discount. Instead of our 20% fee, you will only pay a 15% fee!</p>
              </div>
              <div className='mb-4 mt-4 flex flex-col items-center'>
                <p className='text-gray-600'>Our Fee</p>
                <p className='font-bold text-6xl text-gray-600'>15%</p>
                <p className='font-bold text-xl'>${(customerData.customerLeadData.leadAmount * 0.15).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                <p className='font-bold text-xl'>Save ${((customerData.customerLeadData.leadAmount * (customerData.customerDocuments.feePercentage)) - (customerData.customerLeadData.leadAmount * 0.15)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              </div>
              <button disabled={generatingInvoice} onClick={() => handleSelectFeeRoute(0.15, 0)} className={`w-40 h-10 rounded-lg focus:outline-none mt-auto ${generatingInvoice ? 'bg-gray-400 text-gray-200 cursor-not-allowed' : 'bg-primaryColor hover:bg-primaryDarkColor transition duration-200'}`}>Pay now</button>
            </div>
            <div className='lg:min-w-px lg:h-[15rem] h-[1px] max-sm:mt-4 max-sm:mb-4 bg-gray-400'></div>
            <div className='flex flex-col items-center flex-1'>
              <div className='mb-4'>
                <p className='text-gray-600 font-bold text-xl text-start'>Option 2</p>
                <p className='text-gray-600 mt-2 text-left'>Provide your charge card information or banking information via Plaid, and we will deduct or charge you the {customerData.customerDocuments.feePercentage * 100}% fee AFTER you receive your check from the IRS.</p>
              </div>
              <div className='mb-4 mt-4 flex flex-col items-center'>
                <p className='text-gray-600'>Our Fee</p>
                <p className='font-bold text-6xl text-gray-600'>{customerData.customerDocuments.feePercentage * 100}%</p>
                <p className='font-bold text-xl'>${(customerData.customerLeadData.leadAmount * (customerData.customerDocuments.feePercentage)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              </div>
              <button disabled={generatingInvoice} onClick={() => handleSelectFeeRoute(customerData.customerDocuments.feePercentage, 1)} className={`w-40 h-10 rounded-lg focus:outline-none mt-auto ${generatingInvoice ? 'bg-gray-400 text-gray-200 cursor-not-allowed' : 'bg-blue-500 text-white hover:bg-blue-600'}`}>Pay After Refund</button>
            </div>
          </div>
          {generatingInvoice && (
            <div className='flex flex-row items-center justify-center gap-2'>
              <div className='loader ease-linear rounded-full border-[6px] border-t-[6px]  h-8 w-8 mt-4 mb-4'></div>
              <p>Generating Invoice...</p>
            </div>
          )}
        </div>
      </div>
    )
  )
}

export function GenerateInvoicePrePaid({ isOpen, setIsOpen, setShowTOS, handleSignedTOS, setTOSCustomerFee, generatingInvoice }) {
  const handleSelectPayment = () => {
    if (customerData.customerTOSData.tosLinkURL.length === 0) {
      setTOSCustomerFee(0.15)
      setShowTOS(true)
    } else
      handleSignedTOS(0.15)
  }

  useEffect(() => {
    if (isOpen)
      posthog.startSessionRecording()
    else
      posthog.stopSessionRecording()
  }, [isOpen])

  return (
    isOpen && (
      <div className='fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-30'>
        <div className='bg-white shadow-md rounded-lg p-10 lg:w-full w-11/12 max-w-[65%] text-center relative'>
          <button onClick={() => setIsOpen(false)} className='absolute right-6 top-6 text-red-700 hover:text-red-900 transition-colors duration-200'>
            <svg xmlns='http://www.w3.org/2000/svg' className='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M6 18L18 6M6 6l12 12' />
            </svg>
          </button>
          <h2 className='text-2xl font-semibold mb-4'>Get Your IRS forms now!</h2>
          <p className='text-gray-600 mb-8 text-start'>
            Your IRS forms are ready to be completed, signed, and mailed. It appears you have a balance owing to the IRS of the amount of <span className='font-bold'>${customerData.customerDocuments.irsBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>.
            For customers that owe back taxes to the IRS, please pay your SETC processing fee now. We've taken your circumstances into consideration and have proactively
            provided a discount. Instead of our 20% fee, you will only pay a 15% fee. Once paid, your IRS forms will be immediately available here in your customer portal.
          </p>
          <p className='text-gray-600 mb-8 font-bold text-xl'>Your refund amount is: ${customerData.customerLeadData.leadAmount.toLocaleString()}</p>
          <div className='flex flex-row justify-between items-stretch space-x-8'>
            <div className='flex flex-col items-center flex-1'>
              <div className='mb-4 mt-4 flex flex-col items-center'>
                <p className='text-gray-600'>Our Fee</p>
                <p className='font-bold text-6xl text-gray-600'>15%</p>
                <p className='font-bold text-xl'>${(customerData.customerLeadData.leadAmount * 0.15).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              </div>
              {generatingInvoice && (
                <div className='flex flex-row items-center justify-center gap-2'>
                  <div className='loader ease-linear rounded-full border-[6px] border-t-[6px]  h-8 w-8 mt-4 mb-4'></div>
                  <p>Generating Invoice...</p>
                </div>
              )}
              <button disabled={generatingInvoice} onClick={handleSelectPayment} className={`w-40 h-10 rounded-lg focus:outline-none mt-auto ${generatingInvoice ? 'bg-gray-400 text-gray-200 cursor-not-allowed' : 'text-white bg-primaryColor hover:bg-primaryDarkColor transition duration-200'}`}>Pay now</button>
            </div>
          </div>
        </div>
      </div>
    )
  )
}